import { Platform } from "react-native";
import {
  radius,
  spacing,
  themes,
  typography,
  type EffortRampStop,
  type HeatmapRamp,
  type IntensityScale,
  type Palette,
  type ThemeFamily,
} from "@jackedlogs/shared";

// The shared `typography.fonts` values are web CSS stacks. React Native's
// `fontFamily` accepts only a single registered family name, so on native the
// CSS stack is silently dropped and text falls back to the system font (the
// reason the "scoreboard" identity never rendered on mobile, and mono numbers
// weren't actually mono). Resolve a platform-correct set once, here: web keeps
// the stack (its @font-face is loaded in index.css); native uses the bare names
// registered in MainApplication via ReactFontManager.addCustomFont.
export const platformTypography = {
  ...typography,
  fonts: {
    sans: Platform.select({
      default: typography.fonts.sans,
      native: "Archivo",
    }) as string,
    mono: Platform.select({
      default: typography.fonts.mono,
      native: "JetBrains Mono",
    }) as string,
  },
} as const;

// react-native-web's Pressable does not implement `hitSlop` at all (DS-48),
// so any box under 44pt that relies on hitSlop to reach the tap-target floor
// silently fails on web while working fine natively. Apply as
// `minWidth`/`minHeight: webTapFloor` on the affected box; `undefined` on
// native is a no-op, leaving the already-shipped visual size (hitSlop does
// its job there) untouched.
export const webTapFloor = Platform.OS === "web" ? 44 : undefined;

// Shared-component theme: the host resolves dark/light (it owns the OS
// scheme / user preference), the component resolves the token set from
// the flag. Same shape apps/mobile's useTheme() builds (intensity scale
// included since the IntensityGridView lift) — so a host theme object is
// structurally assignable where a UiTheme is expected.
export type UiTheme = {
  palette: Palette;
  spacing: typeof spacing;
  radius: typeof radius;
  typography: typeof platformTypography;
  intensity: IntensityScale;
  // Worked-muscle volume heatmap ramp (5-step, low→high), already resolved for
  // this family + mode, so bodymap consumers read it off the theme instead of
  // importing the brand-only `heatmapRamp` and re-indexing by scheme.
  // DS-RAMP5 — the 5-tuple type, not `readonly string[]`: it carries the ramp's
  // exactly-5 guarantee (guarded at every ThemeDefinition) across the resolve
  // boundary, so `resolveTheme` cannot widen a wrong-length ramp in unnoticed.
  heatmapRamp: HeatmapRamp;
  // Effort-meter ramp (5-step, easy→hard, monochrome on the family accent),
  // resolved per family + mode like heatmapRamp. Each stop pairs the segment
  // fill with the AA-validated ink for the step number.
  effortRamp: readonly EffortRampStop[];
};

// Resolve the token set for a given dark flag AND theme family. The `family`
// default is "brand" — a bare `resolveTheme(dark)` behaves EXACTLY as before
// this module learned about families (back-compat for tests / any caller that
// doesn't opt in). The APP default (Metria) is NOT here: it lives in the
// persisted ThemePreference (schemas/profile.ts) and flows through
// ThemeContext, so real consumers always pass the user's chosen family.
export function resolveTheme(
  dark: boolean,
  family: ThemeFamily = "brand",
): UiTheme {
  const def = themes[family];
  const mode = dark ? "dark" : "light";
  return {
    palette: def.palette[mode] as Palette,
    spacing,
    radius,
    typography: platformTypography,
    intensity: def.intensityScale[mode] as IntensityScale,
    heatmapRamp: def.heatmapRamp[mode],
    effortRamp: def.effortRamp[mode],
  };
}
